import {
  getCertainOrgFiltersMap,
  getContentAccuracyWidgetDynamicFilters,
  getOrgFiltersMap
} from "../../helpers"
import { FiltersState } from "../../states"
import { ContentType, WidgetDataParams, WidgetName } from "../../types"
import {
  CONTENT_TYPES_TO_USAGE,
  PROGRAM_FILTER_REQUEST_PARAM,
  SUBJECT_FILTER_REQUEST_PARAM
} from "../filters"
import { ContentTypeLabels } from "../labels"

export const CONTENT_WIDGETS_REQUEST_PARAMS: Partial<
  Record<WidgetName, WidgetDataParams>
> = {
  ContentTypePracticeTests: {
    type: "contentType",
    label: ContentTypeLabels[ContentType.practiceTest],
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      // {
      //   measures: [
      // "content_view_tests.assignedPercent"
      // "content_view_tests.completedPercent"
      //   ],
      //   timeDimensions: [
      //     {
      //       dimension: "content_view_tests.completedAt",
      //       dateRange: ""
      //     }
      //   ]
      // },
      { measures: ["content_view_tests.count"] }
    ],
    fields: {
      count: "content_view_tests.count",
      date: "content_view_tests.completedAt"
      // assignedPercent: "content_view_tests.assignedPercent",
      // completedPercent: "content_view_tests.completedPercent"
    },
    contentType: ContentType.practiceTest,
    displayConfig: {
      tooltip:
        "Full-length practice tests that prepare students for official assessments and track their progress."
    }
  },
  ContentTypeMasteryLessons: {
    type: "contentType",
    label: ContentTypeLabels[ContentType.masteryLesson],
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      // {
      //   /*
      //     From here and below: "% Assigned" metric is postponed
      //     for all content widgets except Practice Tests until Assignment Tool is done.
      //     See https://bam-boo.atlassian.net/browse/MRM-647.
      //   */
      //   measures: [
      //     // "content_view_ml_lessons.assignedPercent",
      //     "content_view_ml_lessons.completedPercent"
      //   ],
      //   timeDimensions: [
      //     {
      //       dimension: "content_view_ml_lessons.completedAt",
      //       dateRange: ""
      //     }
      //   ]
      // },
      { measures: ["content_view_ml_lessons.count"] }
    ],
    fields: {
      count: "content_view_ml_lessons.count",
      assignedPercent: "content_view_ml_lessons.assignedPercent",
      completedPercent: "content_view_ml_lessons.completedPercent",
      date: "content_view_ml_lessons.completedAt"
    },
    contentType: ContentType.masteryLesson,
    displayConfig: {
      tooltip:
        "Lessons featuring videos and interactive exercises to build content mastery and strengthen strategic thinking."
    }
  },
  ContentTypeBellRingers: {
    type: "contentType",
    label: ContentTypeLabels[ContentType.bellRinger],
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          // "content_view_br_lessons.assignedPercent",
          "content_view_br_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_br_lessons.completedAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_br_lessons.count"] }
    ],
    fields: {
      count: "content_view_br_lessons.count",
      assignedPercent: "content_view_br_lessons.assignedPercent",
      completedPercent: "content_view_br_lessons.completedPercent",
      date: "content_view_br_lessons.completedAt"
    },
    contentType: ContentType.bellRinger,
    displayConfig: {
      tooltip:
        "A set of standards-aligned practice questions used to warm students up at the beginning of class and reinforce mastery of key skills."
    }
  },
  ContentTypeMasteryCurriculum: {
    type: "contentType",
    label: ContentTypeLabels[ContentType.masteryCurriculum],
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          // "content_view_mc_lessons.assignedPercent",
          "content_view_mc_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_mc_lessons.createdAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_mc_lessons.count"] }
    ],
    fields: {
      count: "content_view_mc_lessons.count",
      assignedPercent: "content_view_mc_lessons.assignedPercent",
      completedPercent: "content_view_mc_lessons.completedPercent",
      date: "content_view_mc_lessons.createdAt"
    },
    contentType: ContentType.masteryCurriculum,
    displayConfig: {
      tooltip:
        "Chapters available in print or ebook format, providing exercises and resources aligned to state and test standards, with the option to complete practice questions directly online."
    }
  },
  ContentTypeSkillReview: {
    type: "contentType",
    label: ContentTypeLabels[ContentType.skillReview],
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          // "content_view_sh_lessons.assignedPercent",
          "content_view_sh_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_sh_lessons.createdAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_sh_lessons.count"] }
    ],
    fields: {
      count: "content_view_sh_lessons.count",
      assignedPercent: "content_view_sh_lessons.assignedPercent",
      completedPercent: "content_view_sh_lessons.completedPercent",
      date: "content_view_sh_lessons.createdAt"
    },
    contentType: ContentType.skillReview,
    displayConfig: {
      tooltip:
        "Targeted review modules featuring short instructional videos and focused practice, helping students reinforce and strengthen specific skills."
    }
  },
  // Average Score
  ContentTypePracticeTestsAverageScore: {
    type: "contentAverageScore",
    label: "Average Score",
    entity: "content-library",
    contentType: ContentType.practiceTest
  },
  // ----
  // Average Accuracy
  ContentTypeMasteryCurriculumAverageAccuracy: {
    type: "contentAverageAccuracy",
    entity: "content-library",
    allowedFilters: ["test", "state", "district", "school", "student", "grade"],
    requests: [
      {
        measures: [
          "content_view_mc_lessons.accuracy",
          "content_view_mc_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_mc_lessons.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      accuracy: "content_view_mc_lessons.accuracy",
      completedCount: "content_view_mc_lessons.completedCount",
      date: "content_view_mc_lessons.createdAt"
    },
    contentType: ContentType.masteryCurriculum,
    dynamicFilters: ["class", "teacher"],
    getDynamicFilters: getContentAccuracyWidgetDynamicFilters(
      "content_view_mc_lessons"
    )
  },
  ContentTypeMasteryLessonsAverageAccuracy: {
    type: "contentAverageAccuracy",
    entity: "content-library",
    allowedFilters: ["test", "state", "district", "school", "student", "grade"],
    requests: [
      {
        measures: [
          "content_view_ml_lessons.accuracy",
          "content_view_ml_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_ml_lessons.completedAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      accuracy: "content_view_ml_lessons.accuracy",
      completedCount: "content_view_ml_lessons.completedCount",
      date: "content_view_ml_lessons.completedAt"
    },
    contentType: ContentType.masteryLesson,
    dynamicFilters: ["class", "teacher"],
    getDynamicFilters: getContentAccuracyWidgetDynamicFilters(
      "content_view_ml_lessons"
    )
  },
  ContentTypeSkillReviewAverageAccuracy: {
    type: "contentAverageAccuracy",
    entity: "content-library",
    allowedFilters: ["test", "state", "district", "school", "student", "grade"],
    requests: [
      {
        measures: [
          "content_view_sh_lessons.accuracy",
          "content_view_sh_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_sh_lessons.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      accuracy: "content_view_sh_lessons.accuracy",
      completedCount: "content_view_sh_lessons.completedCount",
      date: "content_view_sh_lessons.createdAt"
    },
    contentType: ContentType.skillReview,
    dynamicFilters: ["class", "teacher"],
    getDynamicFilters: getContentAccuracyWidgetDynamicFilters(
      "content_view_sh_lessons"
    )
  },
  // ----
  // Average Time Spent
  ContentTypeBellRingersAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "test",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        filters: [
          {
            values: [...CONTENT_TYPES_TO_USAGE[ContentType.bellRinger]],
            member: "avegare_time_spent_view.contentType",
            operator: "equals"
          }
        ],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.bellRinger,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypePracticeTestAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    label: "Average Time Spent",
    entity: "content-library",
    allowedFilters: [
      "test",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        filters: [
          {
            values: [...CONTENT_TYPES_TO_USAGE[ContentType.practiceTest]],
            member: "avegare_time_spent_view.contentType",
            operator: "equals"
          }
        ],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.practiceTest,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeMasteryCurriculumAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "test",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        filters: [
          {
            values: [...CONTENT_TYPES_TO_USAGE[ContentType.masteryCurriculum]],
            member: "avegare_time_spent_view.contentType",
            operator: "equals"
          }
        ],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.masteryCurriculum,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeMasteryLessonsAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "test",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        filters: [
          {
            values: [...CONTENT_TYPES_TO_USAGE[ContentType.masteryLesson]],
            member: "avegare_time_spent_view.contentType",
            operator: "equals"
          }
        ],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.masteryLesson,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeSkillReviewAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "test",
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        filters: [
          {
            values: [...CONTENT_TYPES_TO_USAGE[ContentType.skillReview]],
            member: "avegare_time_spent_view.contentType",
            operator: "equals"
          }
        ],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.skillReview,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  // ----
  // Entity Average Time Spent
  ContentTypeEntityBellRingersAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: ["state", "district", "school", "teacher"],
    requests: [
      {
        dimensions: ["content_view_br_lessons.averageTimeSpent"],
        timeDimensions: [
          {
            dimension: "content_view_br_lessons.completedAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "content_view_br_lessons.averageTimeSpent",
      date: "content_view_br_lessons.completedAt"
    },
    contentType: ContentType.bellRinger,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeEntityPracticeTestAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: ["state", "district", "school", "student"],
    requests: [
      {
        dimensions: ["content_view_tests.averageTimeSpent"],
        timeDimensions: [
          {
            dimension: "content_view_tests.completedAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "content_view_tests.averageTimeSpent",
      date: "content_view_tests.completedAt"
    },
    contentType: ContentType.practiceTest,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeEntityMasteryCurriculumAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.masteryCurriculum,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeEntityMasteryLessonsAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.masteryLesson,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  ContentTypeEntitySkillReviewAverageTimeSpent: {
    type: "contentAverageTimeSpent",
    entity: "content-library",
    allowedFilters: [
      "state",
      "district",
      "school",
      "teacher",
      "class",
      "student",
      "grade"
    ],
    requests: [
      {
        measures: ["avegare_time_spent_view.avgTimeSpent"],
        timeDimensions: [
          {
            dimension: "avegare_time_spent_view.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      averageTimeSpent: "avegare_time_spent_view.avgTimeSpent",
      date: "avegare_time_spent_view.createdAt"
    },
    contentType: ContentType.skillReview,
    displayConfig: {
      tooltip: "The amount of time students spent on the content."
    }
  },
  // ----
  // Completed
  ContentTypeBellRingersCompletedPercent: {
    type: "contentCompletedPercent",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_br_lessons.assignedCount",
          "content_view_br_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_br_lessons.completedAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      assignedCount: "content_view_br_lessons.assignedCount",
      completedPercent: "content_view_br_lessons.completedPercent",
      date: "content_view_br_lessons.completedAt"
    },
    contentType: ContentType.bellRinger
  },
  ContentTypePracticeTestsCompletedPercent: {
    type: "contentCompletedPercent",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_tests.assignedCount",
          "content_view_tests.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_tests.completedAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      assignedCount: "content_view_tests.assignedCount",
      completedPercent: "content_view_tests.completedPercent",
      date: "content_view_tests.completedAt"
    },
    contentType: ContentType.practiceTest
  },
  ContentTypeMasteryCurriculumCompletedPercent: {
    type: "contentCompletedPercent",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_mc_lessons.assignedCount",
          "content_view_mc_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_mc_lessons.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      assignedCount: "content_view_mc_lessons.assignedCount",
      completedPercent: "content_view_mc_lessons.completedPercent",
      date: "content_view_mc_lessons.createdAt"
    },
    contentType: ContentType.masteryCurriculum
  },
  ContentTypeMasteryLessonsCompletedPercent: {
    type: "contentCompletedPercent",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_ml_lessons.assignedCount"
          // "content_view_ml_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_ml_lessons.completedAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      assignedCount: "content_view_ml_lessons.assignedCount",
      completedPercent: "content_view_ml_lessons.completedPercent",
      date: "content_view_ml_lessons.completedAt"
    },
    contentType: ContentType.masteryLesson
  },
  ContentTypeSkillReviewCompletedPercent: {
    type: "contentCompletedPercent",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_sh_lessons.assignedCount",
          "content_view_sh_lessons.completedPercent"
        ],
        timeDimensions: [
          {
            dimension: "content_view_sh_lessons.createdAt",
            dateRange: ""
          }
        ]
      }
    ],
    fields: {
      assignedCount: "content_view_sh_lessons.assignedCount",
      completedPercent: "content_view_sh_lessons.completedPercent",
      date: "content_view_sh_lessons.createdAt"
    },
    contentType: ContentType.skillReview
  },
  // ----
  // Assigned
  ContentPracticeTestAssigned: {
    type: "contentAssigned",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_tests.assignedPercent",
          "content_view_tests.completedPercent"
          // "content_view_tests.assignedCount",
          // "content_view_tests.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_tests.completedAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_tests.count"] }
    ],
    fields: {
      count: "content_view_tests.count",
      assignedPercent: "content_view_tests.assignedPercent",
      completedPercent: "content_view_tests.completedPercent",
      date: "content_view_tests.completedAt"
      // assignedCount: "content_view_tests.assignedCount",
      // completedCount: "content_view_tests.completedCount"
    },
    contentType: ContentType.practiceTest
  },
  ContentBellRingersAssigned: {
    type: "contentAssigned",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_br_lessons.assignedPercent",
          "content_view_br_lessons.completedPercent"
          // "content_view_br_lessons.assignedCount",
          // "content_view_br_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_br_lessons.completedAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_br_lessons.count"] }
    ],
    fields: {
      count: "content_view_br_lessons.count",
      assignedPercent: "content_view_br_lessons.assignedPercent",
      completedPercent: "content_view_br_lessons.completedPercent",
      date: "content_view_br_lessons.completedAt"
      // assignedCount: "content_view_br_lessons.assignedCount",
      // completedCount: "content_view_br_lessons.completedCount"
    },
    contentType: ContentType.bellRinger
  },
  ContentMasteryCurriculumAssigned: {
    type: "contentAssigned",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_mc_lessons.assignedPercent",
          "content_view_mc_lessons.completedPercent"
          // "content_view_mc_lessons.assignedCount",
          // "content_view_mc_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_mc_lessons.createdAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_mc_lessons.count"] }
    ],
    fields: {
      count: "content_view_mc_lessons.count",
      assignedPercent: "content_view_mc_lessons.assignedPercent",
      completedPercent: "content_view_mc_lessons.completedPercent",
      date: "content_view_mc_lessons.createdAt"
      // assignedCount: "content_view_mc_lessons.assignedCount",
      // completedCount: "content_view_mc_lessons.completedCount"
    },
    contentType: ContentType.masteryCurriculum
  },
  ContentMasteryLessonsAssigned: {
    type: "contentAssigned",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_ml_lessons.assignedPercent"
          // "content_view_ml_lessons.completedPercent"
          // "content_view_ml_lessons.assignedCount",
          // "content_view_ml_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_ml_lessons.completedAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_ml_lessons.count"] }
    ],
    fields: {
      count: "content_view_ml_lessons.count",
      assignedPercent: "content_view_ml_lessons.assignedPercent",
      completedPercent: "content_view_ml_lessons.completedPercent",
      date: "content_view_ml_lessons.completedAt"
      // assignedCount: "content_view_ml_lessons.assignedCount",
      // completedCount: "content_view_ml_lessons.completedCount"
    },
    contentType: ContentType.masteryLesson
  },
  ContentSkillReviewAssigned: {
    type: "contentAssigned",
    entity: "content-library",
    allowedFilters: ["test"],
    requests: [
      {
        measures: [
          "content_view_sh_lessons.assignedPercent",
          "content_view_sh_lessons.completedPercent"
          // "content_view_sh_lessons.assignedCount",
          // "content_view_sh_lessons.completedCount"
        ],
        timeDimensions: [
          {
            dimension: "content_view_sh_lessons.createdAt",
            dateRange: ""
          }
        ]
      },
      { measures: ["content_view_sh_lessons.count"] }
    ],
    fields: {
      count: "content_view_sh_lessons.count",
      assignedPercent: "content_view_sh_lessons.assignedPercent",
      completedPercent: "content_view_sh_lessons.completedPercent",
      date: "content_view_sh_lessons.createdAt"
      // assignedCount: "content_view_sh_lessons.assignedCount",
      // completedCount: "content_view_sh_lessons.completedCount"
    },
    contentType: ContentType.skillReview
  }
  // ----
}

export const CONTENT_WIDGETS_BY_VIEW: Record<string, WidgetName[]> = {
  "content-library": [
    //"ContentTypePracticeTests",
    //"ContentTypeMasteryLessons",
    //"ContentTypeBellRingers",
    //"ContentTypeMasteryCurriculum",
    //"ContentTypeSkillReview"
  ],
  //"content-library/element": ["ContentTypeBellRingers"],
  //"content-library/study-hall": ["ContentTypeSkillReview"],
  //"content-library/practice-test": ["ContentTypePracticeTests"],
  //"content-library/mastery-lesson": ["ContentTypeMasteryLessons"],
  //"content-library/mastery-curriculum": ["ContentTypeMasteryCurriculum"]
}

export const CONTENT_WIDGETS_ADDITIONAL_FILTERS: Record<
  string,
  { name: WidgetName; requestParam: string }[]
> = {
  "content-library/practice-test_id": [
    //{
    //  name: "ContentTypePracticeTestsAverageScore",
    //  requestParam: "content_view_tests.id"
    //},
    //{
    //  name: "ContentTypePracticeTestsCompletedPercent",
    //  requestParam: "content_view_tests.id"
    //},
    //{
    //  name: "ContentTypeEntityPracticeTestAverageTimeSpent",
    //  requestParam: "content_view_tests.id"
    //}
  ],
  "content-library/element_id": [
    {
      name: "ContentTypeBellRingersCompletedPercent",
      requestParam: "content_view_br_lessons.id"
    },
    {
      name: "ContentTypeEntityBellRingersAverageTimeSpent",
      requestParam: "content_view_br_lessons.id"
    }
  ],
  "content-library/mastery-lesson_id": [
    {
      name: "ContentTypeMasteryLessonsAverageAccuracy",
      requestParam: "content_view_ml_lessons.id"
    },
    {
      name: "ContentTypeMasteryLessonsCompletedPercent",
      requestParam: "content_view_ml_lessons.id"
    },
    {
      name: "ContentTypeEntityMasteryLessonsAverageTimeSpent",
      requestParam: "avegare_time_spent_view.contentId"
    }
  ],
  "content-library/mastery-curriculum_id": [
    {
      name: "ContentTypeMasteryCurriculumAverageAccuracy",
      requestParam: "content_view_mc_lessons.id"
    },
    {
      name: "ContentTypeMasteryCurriculumCompletedPercent",
      requestParam: "content_view_mc_lessons.id"
    },
    {
      name: "ContentTypeEntityMasteryCurriculumAverageTimeSpent",
      requestParam: "avegare_time_spent_view.contentId"
    }
  ],
  "content-library/study-hall_id": [
    {
      name: "ContentTypeSkillReviewAverageAccuracy",
      requestParam: "content_view_sh_lessons.id"
    },
    {
      name: "ContentTypeSkillReviewCompletedPercent",
      requestParam: "content_view_sh_lessons.id"
    },
    {
      name: "ContentTypeEntitySkillReviewAverageTimeSpent",
      requestParam: "avegare_time_spent_view.contentId"
    }
  ]
}

export const CONTENT_WIDGETS_BY_FILTER: Record<string, WidgetName[]> = {
  //[ContentType.bellRinger]: ["ContentTypeBellRingers"],
  //[ContentType.masteryLesson]: ["ContentTypeMasteryLessons"],
  //[ContentType.practiceTest]: ["ContentTypePracticeTests"],
  //[ContentType.masteryCurriculum]: ["ContentTypeMasteryCurriculum"],
  //[ContentType.skillReview]: ["ContentTypeSkillReview"]
}

export const CONTENT_WIDGETS_FILTER_MAP: Partial<
  Record<WidgetName, Record<string, string>>
> = {
  ContentTypePracticeTests: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_tests.program"
  },
  ContentTypeMasteryLessons: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.subject"
  },
  ContentTypeBellRingers: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_br_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_br_lessons.subject"
  },
  ContentTypeMasteryCurriculum: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.subject"
  },
  ContentTypeSkillReview: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.subject"
  },
  ContentTypeMasteryCurriculumAverageAccuracy: {
    ...getOrgFiltersMap("content_view_mc_lessons"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.subject"
  },
  ContentTypeMasteryLessonsAverageAccuracy: {
    ...getOrgFiltersMap("content_view_ml_lessons"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.subject"
  },
  ContentTypeSkillReviewAverageAccuracy: {
    ...getOrgFiltersMap("content_view_sh_lessons"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.subject"
  },
  ContentTypeBellRingersAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.subject"
  },
  ContentTypePracticeTestAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.subject"
  },
  ContentTypeMasteryCurriculumAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.subject"
  },
  ContentTypeMasteryLessonsAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.subject"
  },
  ContentTypeSkillReviewAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view"),
    [PROGRAM_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "avegare_time_spent_view.subject"
  },
  // Entity Average Time Spent
  ContentTypeEntityBellRingersAverageTimeSpent: {
    ...getCertainOrgFiltersMap("content_view_br_lessons", [
      "state",
      "district",
      "school",
      "teacher"
    ])
  },
  ContentTypeEntityPracticeTestAverageTimeSpent: {
    ...getCertainOrgFiltersMap("content_view_tests", [
      "state",
      "district",
      "school",
      "student"
    ])
  },
  ContentTypeEntityMasteryCurriculumAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view")
  },
  ContentTypeEntityMasteryLessonsAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view")
  },
  ContentTypeEntitySkillReviewAverageTimeSpent: {
    ...getOrgFiltersMap("avegare_time_spent_view")
  },
  // ----
  ContentTypeBellRingersCompletedPercent: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_br_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_br_lessons.subject"
  },
  ContentTypePracticeTestsCompletedPercent: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_tests.program"
  },
  ContentTypeMasteryCurriculumCompletedPercent: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.subject"
  },
  ContentTypeMasteryLessonsCompletedPercent: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.subject"
  },
  ContentTypeSkillReviewCompletedPercent: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.subject"
  },
  ContentPracticeTestAssigned: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_tests.program"
  },
  ContentBellRingersAssigned: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_br_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_br_lessons.subject"
  },
  ContentMasteryCurriculumAssigned: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_mc_lessons.subject"
  },
  ContentMasteryLessonsAssigned: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_ml_lessons.subject"
  },
  ContentSkillReviewAssigned: {
    [PROGRAM_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.program",
    [SUBJECT_FILTER_REQUEST_PARAM]: "content_view_sh_lessons.subject"
  }
}

/*
  "% Complete" widgets for all content types except Practice Test are postponed.
  See https://bam-boo.atlassian.net/browse/MRM-647.
*/
export const ENTITY_CONTENT_TYPE_WIDGETS: Record<string, WidgetName[]> = {
  element: [
    //"ContentTypeBellRingers",
    // "ContentTypeBellRingersCompletedPercent",
    //"ContentTypeEntityBellRingersAverageTimeSpent"
  ],
  "mastery-curriculum": [
    //"ContentTypeMasteryCurriculum",
    // "ContentTypeMasteryCurriculumCompletedPercent",
    //"ContentTypeMasteryCurriculumAverageAccuracy",
    //"ContentTypeEntityMasteryCurriculumAverageTimeSpent"
  ],
  "mastery-lesson": [
    //"ContentTypeMasteryLessons",
    // "ContentTypeMasteryLessonsCompletedPercent",
    //"ContentTypeMasteryLessonsAverageAccuracy",
    //"ContentTypeEntityMasteryLessonsAverageTimeSpent"
  ],
  "practice-test": [
    //"ContentTypePracticeTests",
    //"ContentTypePracticeTestsAverageScore",
    // "ContentTypePracticeTestsCompletedPercent",
    //"ContentTypeEntityPracticeTestAverageTimeSpent"
  ],
  "study-hall": [
    //"ContentTypeSkillReview",
    // "ContentTypeSkillReviewCompletedPercent",
    // "ContentTypeSkillReviewAverageAccuracy",
    //"ContentTypeEntitySkillReviewAverageTimeSpent"
  ]
}

/*
  "% Assigned" widgets for all content types except Practice Test are postponed.
  See https://bam-boo.atlassian.net/browse/MRM-647.
*/
export const SINGLE_CONTENT_TYPE_WIDGETS: Record<ContentType, WidgetName[]> = {
  [ContentType.bellRinger]: [
    //"ContentTypeBellRingers",
    // "ContentBellRingersAssigned",
    // "ContentTypeBellRingersAverageTimeSpent" // Hidden per requirement
  ],
  [ContentType.masteryCurriculum]: [
    //"ContentTypeMasteryCurriculum",
    // "ContentMasteryCurriculumAssigned",
    //"ContentTypeMasteryCurriculumAverageAccuracy",
    //"ContentTypeMasteryCurriculumAverageTimeSpent"
  ],
  [ContentType.masteryLesson]: [
    //"ContentTypeMasteryLessons",
    // "ContentMasteryLessonsAssigned",
    //"ContentTypeMasteryLessonsAverageAccuracy",
    //"ContentTypeMasteryLessonsAverageTimeSpent"
  ],
  [ContentType.practiceTest]: [
    //"ContentTypePracticeTests",
    // "ContentPracticeTestAssigned",
    //"ContentTypePracticeTestsAverageScore"
  ],
  [ContentType.skillReview]: [
    //"ContentTypeSkillReview",
    // "ContentSkillReviewAssigned",
    // "ContentTypeSkillReviewAverageAccuracy",
    //"ContentTypeSkillReviewAverageTimeSpent"
  ]
}
